import React, { FC, useState } from "react";

import classNames from "classnames";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { DailyTopPick, FixtureItem } from "@api";

import { formatOdds, formatProbability, formatTipName } from "@utils";

import {
  TeamsHeader,
  TeamsInfoItem,
  useTeamsHeaderProps,
} from "@ui/components";
import { Box, Button, Icon, Tag, Text, Title } from "@ui/elements";
import { Stack } from "@ui/layout";

import {
  MatchDetailsDrawer,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModal,
} from "@components";

import { formatTipCategory } from "../../utils/formatTipCategory";

import "./DailyTopPicks.scss";

const fixture: FixtureItem = {
  fixture: {
    date: "2025-03-13T17:45:00+00:00",
    venue: {
      name: "Stadio Olimpico",
      city: "Roma",
      id: 910,
    },
    timezone: "UTC",
    periods: {
      first: 12,
      second: 12,
    },
    id: 1353195,
    referee: "D. Makkelie",
    timestamp: 1741887900,
    status: {
      elapsed: 12,
      short: "NS",
      long: "Not Started",
    },
  },
  teams: {
    home: {
      name: "Lazio",
      logo: "https://media.api-sports.io/football/teams/487.png",
      id: 487,
      winner: null,
    },
    away: {
      name: "Plzen",
      logo: "https://media.api-sports.io/football/teams/567.png",
      id: 567,
      winner: null,
    },
  },
  score: {
    fulltime: {
      home: null,
      away: null,
    },
    extratime: {
      home: null,
      away: null,
    },
    halftime: {
      home: null,
      away: null,
    },
    penalty: {
      home: null,
      away: null,
    },
  },
  goals: {
    home: null,
    away: null,
  },
  league: {
    country: "World",
    flag: "",
    round: "Round of 16",
    name: "UEFA Europa League",
    logo: "https://media.api-sports.io/football/leagues/3.png",
    season: 2024,
    id: 3,
  },
};

const tip: DailyTopPick = {
  season: 2024,
  fixture_id: "1353195",
  league_id: 3,
  start_date: "2025-03-13T17:45:00+00:00",
  id: "c1c38063-7c3e-436f-8497-e53126c692cd",
  rank: 2,
  type: "value",
  edge: 12.19653179190751,
  rating: "value",
  odds: 1.73,
  name: "Over 2.5",
  best_choice: false,
  probability: 70,
  bet_amount: 2,
  category: "Total Goals Over/Under",
  reasoning:
    "Lazio’s attacking prowess at home combined with Plzen’s incentive to push for goals suggests a match with a high goal tally. With odds at 1.73, this bet provides good value despite a slightly lower probability, warranting a moderate stake.",
};

type LockedTipBoxProps = {
  tipsAvailable: number;
};

export const LockedTipBox: FC<LockedTipBoxProps> = ({ tipsAvailable }) => {
  const teamsHeaderProps = useTeamsHeaderProps(fixture);
  const [showFullReasoning, setShowFullReasoning] = useState(false);
  const [matchDetailsOpen, setMatchDetailsOpen] = useState(false);
  const { t } = useTranslation();
  const modals = useModal();

  return (
    <>
      <Box className="top-tip-box">
        <div className="locked-overlay">
          <Stack direction="column" justify="center" align="center">
            <div className="locked-overlay__lock">
              <Icon icon="LockSimple" weight="duotone" />
            </div>
            <Stack direction="column" gap="xxs" justify="center" align="center">
              <Title size={5}>{t("locked-pick")}</Title>
              <Text>{t("choose-plan-to-unlock")}</Text>
            </Stack>

            <Button color="warning" onClick={() => modals.open("subscription")}>
              <Icon icon="CrownSimple" weight="duotone" />
              <span>{t("labels.subscribe")}</span>
            </Button>
          </Stack>
        </div>
        <MatchDetailsDrawer
          isOpen={matchDetailsOpen}
          onClose={() => setMatchDetailsOpen(false)}
          fixture={fixture}
        />
        <Stack direction="column">
          <Stack
            direction="row"
            justify="between"
            className="top-pick-box-header"
            align="center"
          >
            <Stack align="center">
              <div className="top-pick-box-header__rank">{tip.rank}</div>
              <Tag
                type={tip.type === "safe" ? "success" : "info"}
                variant="light"
              >
                {t(`rating.${tip.type}`)}
              </Tag>
            </Stack>
            <Button
              variant="text"
              size="small"
              onClick={() => setMatchDetailsOpen(true)}
            >
              <span>{t("labels.view-ai-analysis")}</span>
              <Icon icon="CaretRight" />
            </Button>
          </Stack>
          <div
            className="teams-header-wrapper"
            onClick={() => setMatchDetailsOpen(true)}
          >
            <TeamsHeader
              {...teamsHeaderProps}
              subtitle={
                <>
                  <TeamsInfoItem
                    icon="Alarm"
                    label={dayjs(fixture.fixture.date).format("HH:mm")}
                  />
                  <TeamsInfoItem icon="Trophy" label={fixture.league.name} />
                </>
              }
            />
          </div>

          <Stack justify="between" className="tip-recommendation-box">
            <Stack direction="column" gap="xxs">
              <Text variant="secondary">{formatTipCategory(tip.category)}</Text>
              <Title size={5}>{formatTipName(tip.category, tip.name)}</Title>
            </Stack>

            <Stack direction="column" gap="xxs" justify="end">
              <Text variant="secondary" align="right">
                {t("odds")}
              </Text>
              <Title size={5} align="right">
                {formatOdds(tip.odds)}
              </Title>
            </Stack>
          </Stack>

          <Stack direction="column" align="start" gap="xxs">
            <Text
              variant="secondary"
              className={classNames("top-pick-reasoning", {
                "line-clamp-2": !showFullReasoning,
              })}
            >
              {tip.reasoning}
            </Text>
            <a
              className="tip-reasoning-toggle"
              onClick={() => {
                setShowFullReasoning(!showFullReasoning);
              }}
            >
              {showFullReasoning
                ? t("labels.show-less")
                : t("labels.show-more")}
            </a>
          </Stack>

          <Stack justify="between">
            <Text>
              <strong>{t("probability")}: </strong>
              <span>{formatProbability(tip.probability)}</span>
            </Text>
            <Text hasIcon>
              <strong>{t("Edge")}: </strong>
              {formatProbability(tip.edge)}
              <Tooltip placement="bottom-end">
                <TooltipTrigger>
                  <Icon className="has-text-text" icon="Info" size="small" />
                </TooltipTrigger>
                <TooltipContent>
                  <Box className="tooltip-box">
                    <Text>{t("edgeInfo")}</Text>
                  </Box>
                </TooltipContent>
              </Tooltip>
            </Text>
          </Stack>
        </Stack>
      </Box>
      {tipsAvailable > 1 && (
        <div className="remaining-tips-unlock-box">
          <Stack direction="column" justify="center" align="center">
            <div className="remaining-tips-unlock-box__lock">
              <Icon icon="LockSimple" weight="duotone" />
            </div>
            <Stack direction="column" gap="xxs" justify="center" align="center">
              <Title size={5}>
                {t("x-more-available", { count: tipsAvailable - 1 })}
              </Title>
              <Text>{t("subscribe-to-unlock-all")}</Text>
            </Stack>

            <Button color="warning" onClick={() => modals.open("subscription")}>
              <Icon icon="CrownSimple" weight="duotone" />
              <span>{t("labels.subscribe")}</span>
            </Button>
          </Stack>
        </div>
      )}
    </>
  );
};
